import { api, isApiError, problemKey } from '@/shared/api';
import { unwrap } from '@/shared/api/envelope';

import {
    toOrderReport,
    toPdfBacklog,
    toReport,
    toReportLinkStatistics,
    toReportEnclosedInvoice,
    toReportShare,
    type IssuedReportShare,
    type OrderReport,
    type PdfBacklog,
    type Report,
    type ReportApproval,
    type ReportFindingAdjustmentInput,
    type ReportInvoiceChoice,
    type ReportResend,
    type ReportLinkStatistics,
    type ReportShare,
    type ReportShareChanges,
} from './model';
import {
    orderReportResponseSchema,
    pdfBacklogResourceSchema,
    reportApprovalResponseSchema,
    reportGenerationResponseSchema,
    reportResourceSchema,
    reportSendResponseSchema,
    reportShareResponseSchema,
    reportSharesResponseSchema,
    reportStatisticsResourceSchema,
} from './types';

export const reportKeys = {
    all: ['reports'] as const,
    /**
     * Nieuwste versie plus de versie die de koper heeft (`OrderReport`). Een andere
     * sleutel dan vroeger (`for-order`, alleen het rapport): een bewaarde cache in
     * de oude vorm wordt zo nooit als nieuwe vorm gelezen.
     */
    forOrder: (orderUuid: string) => ['reports', 'order-report', orderUuid] as const,
    shares: (reportUuid: string) => ['reports', 'shares', reportUuid] as const,
    statistics: (reportUuid: string) => ['reports', 'statistics', reportUuid] as const,
    pdfService: ['reports', 'pdf-service'] as const,
};

/**
 * Schrijfacties gaan nooit in de offline cache en worden niet vanzelf herhaald:
 * goedkeuren en versturen sturen mail, en genereren maakt een nieuwe versie.
 * Zonder `Idempotency-Key` doet de http-client een POST precies één keer.
 */
const WRITE = { cacheKey: false } as const;

/** Bezorgen per mail en sms gebeurt tijdens het verzoek; dat mag even duren. */
const DELIVERY_TIMEOUT_MS = 60_000;

/** De eerste PDF wordt tijdens het verzoek gemaakt. */
const PDF_TIMEOUT_MS = 180_000;

/**
 * Waarom er geen PDF kwam, voor de melding erbij: `setup` als de PDF-dienst op
 * de server ontbreekt (`503 pdf-unavailable` met `setup_required`) - dan helpt
 * opnieuw proberen niet en moet iemand hem instellen -, `temporary` als de dienst
 * er is maar even niet kon, `null` bij elke andere fout.
 */
export type PdfProblem = 'setup' | 'temporary';

export function pdfProblemOf(error: unknown): PdfProblem | null {
    if (problemKey(error) !== 'pdf-unavailable') {
        return null;
    }

    return isApiError(error) && error.problem.setup_required === true ? 'setup' : 'temporary';
}

export interface GeneratedReport {
    report: Report;
    /** `queued` zolang hij wacht of loopt; `finished` of `failed` als hij al klaar was. */
    job: { status: string; reportUuid: string };
}

export interface ReportPdf {
    blob: Blob;
}

/** Rapportage (`02-api-contract.md` §Rapportage). Paden dragen altijd uuid's. */
export const reportApi = {
    /**
     * `GET /orders/{order}/report`: de nieuwste versie, ook als hij nog wordt
     * gegenereerd, en de versie die de koper heeft met haar werkende link.
     * `null` als er voor deze keuring nog geen rapport is.
     */
    async forOrder(orderUuid: string): Promise<OrderReport | null> {
        try {
            return toOrderReport(
                orderReportResponseSchema.parse(await api.get(`/orders/${orderUuid}/report`)),
            );
        } catch (error) {
            if (isApiError(error) && error.status === 404) {
                return null;
            }

            throw error;
        }
    },

    /** `GET /reports/{report}` */
    async find(reportUuid: string): Promise<Report> {
        return toReport(unwrap(reportResourceSchema, await api.get(`/reports/${reportUuid}`)));
    },

    /**
     * `POST /orders/{order}/report` - (een nieuwe versie) genereren. Antwoordt
     * meteen met **202**; het werk loopt in de wachtrij. Een verzoek terwijl er
     * al een generatie loopt, geeft diezelfde versie terug.
     *
     * Standaard met `send: false`: een versie die iemand hier vraagt, leest de
     * eigenaar eerst na, ook als het bedrijf zonder nalezen verstuurt.
     */
    async generate(orderUuid: string, options: { send?: boolean } = {}): Promise<GeneratedReport> {
        const response = reportGenerationResponseSchema.parse(
            await api.post(`/orders/${orderUuid}/report`, { send: options.send ?? false }, WRITE),
        );

        return {
            report: toReport(response.data),
            job: { status: response.meta.job.status, reportUuid: response.meta.job.report_uuid },
        };
    },

    /**
     * `PATCH /reports/{report}/sections/{section}` - tekst van een hoofdstuk
     * vóór goedkeuring. `null` zet de gegenereerde tekst terug.
     */
    async updateSection(
        reportUuid: string,
        sectionUuid: string,
        body: string | null,
    ): Promise<Report> {
        return toReport(
            unwrap(
                reportResourceSchema,
                await api.patch(`/reports/${reportUuid}/sections/${sectionUuid}`, { body }, WRITE),
            ),
        );
    },

    /**
     * `PATCH /reports/{report}/findings/{finding}` - bedragen, NHG-keuze en
     * NHG-onderdeel van één bevinding in een concept, met reden. Het concept
     * wordt meteen opnieuw opgebouwd (zelfde versie); het antwoord is het hele
     * rapport, met nieuwe sleutels voor de bevindingen.
     */
    async adjustFinding(
        reportUuid: string,
        findingKey: string,
        input: ReportFindingAdjustmentInput,
    ): Promise<Report> {
        const amount = (value: number | null) => (value === null ? null : value.toFixed(2));

        return toReport(
            unwrap(
                reportResourceSchema,
                await api.patch(
                    `/reports/${reportUuid}/findings/${findingKey}`,
                    {
                        direct: amount(input.amounts.direct),
                        termijn: amount(input.amounts.termijn),
                        onderhoud: amount(input.amounts.onderhoud),
                        nhg_classification: input.nhgClassification,
                        nhg_code: input.nhgCode,
                        reason: input.reason,
                    },
                    WRITE,
                ),
            ),
        );
    },

    /**
     * `POST /reports/{report}/findings/{finding}/confirm` - de inspecteur is
     * akkoord met een bedrag dat een ander aanpaste.
     */
    async confirmFinding(reportUuid: string, findingKey: string): Promise<Report> {
        return toReport(
            unwrap(
                reportResourceSchema,
                await api.post(`/reports/${reportUuid}/findings/${findingKey}/confirm`, {}, WRITE),
            ),
        );
    },

    /**
     * `POST /reports/{report}/approve` - **goedkeuren is versturen**. Het antwoord
     * draagt de link en, alleen hier, de toegangscode.
     *
     * Met `send: false` wordt het rapport alleen vastgesteld: de link bestaat,
     * maar er gaat niets de deur uit. Kantoor kopieert hem dan zelf.
     *
     * Met `invoice` gaat de factuur als pdf mee in de mail aan de klant, met het eigen
     * bericht: een concept krijgt een nummer, een keuring zonder factuur krijgt er een.
     *
     * Met `language` (een van `report.clientLanguages`) krijgt de klant de mail in die
     * taal, met een link die de klantpagina in die taal opent.
     */
    async approve(
        reportUuid: string,
        options: { send?: boolean; invoice?: ReportInvoiceChoice; language?: string } = {},
    ): Promise<ReportApproval> {
        const response = reportApprovalResponseSchema.parse(
            await api.post(
                `/reports/${reportUuid}/approve`,
                {
                    send: options.send ?? true,
                    ...invoiceBody(options.invoice),
                    ...languageBody(options.language),
                },
                { ...WRITE, timeoutMs: DELIVERY_TIMEOUT_MS },
            ),
        );

        return {
            report: toReport(response.data),
            link: {
                shareUuid: response.meta.share.uuid,
                url: response.meta.share.url,
                validUntil: response.meta.share.valid_until,
                accessCode: response.meta.share.access_code,
            },
            delivery: response.meta.delivery,
            invoice: toReportEnclosedInvoice(response.meta.invoice),
        };
    },

    /**
     * `POST /reports/{report}/revise` - een verstuurd rapport openzetten om iets
     * te corrigeren. Er komt een concept bij met precies de inhoud die verstuurd
     * is, eigen teksten en al; keur je dat goed, dan toont dezelfde link van de
     * klant vanzelf de nieuwe versie. Had de keuring al een concept, dan komt dat
     * concept terug.
     */
    async revise(reportUuid: string): Promise<Report> {
        return toReport(
            unwrap(
                reportResourceSchema,
                await api.post(`/reports/${reportUuid}/revise`, {}, WRITE),
            ),
        );
    },

    /**
     * `POST /reports/{report}/send` - iedereen opnieuw, met de nieuwste link die
     * nog werkt. Werkt er geen meer, dan maakt de API een nieuwe; vraagt die een
     * code, dan staat die - net als na goedkeuren - alleen in dit antwoord.
     *
     * Met `invoice` gaat de factuur mee naar de klant, tenzij die haar al heeft; met
     * `language` krijgt de klant de mail en de link in die taal.
     */
    async send(
        reportUuid: string,
        options: { invoice?: ReportInvoiceChoice; language?: string } = {},
    ): Promise<ReportResend> {
        const response = reportSendResponseSchema.parse(
            await api.post(
                `/reports/${reportUuid}/send`,
                { ...invoiceBody(options.invoice), ...languageBody(options.language) },
                { ...WRITE, timeoutMs: DELIVERY_TIMEOUT_MS },
            ),
        );

        return {
            report: toReport(response.data),
            link: {
                shareUuid: response.meta.share.uuid,
                url: response.meta.share.url,
                validUntil: response.meta.share.valid_until,
                accessCode: response.meta.share.access_code,
            },
            newLink: response.meta.share.new,
            delivery: response.meta.delivery,
            invoice: toReportEnclosedInvoice(response.meta.invoice),
        };
    },

    /**
     * `GET /reports/{report}/pdf` met de sessie van kantoor. De eerste keer
     * wordt hij gemaakt; `409 report-not-ready` zolang het rapport nog
     * genereert, `503 pdf-unavailable` als de PDF-dienst niet kan
     * ({@link pdfProblemOf} zegt of hij ontbreekt of even niet kan).
     */
    async pdf(reportUuid: string): Promise<ReportPdf> {
        const blob = await api.get<Blob>(`/reports/${reportUuid}/pdf`, {
            ...WRITE,
            expect: 'blob',
            timeoutMs: PDF_TIMEOUT_MS,
            retryAttempts: 1,
        });

        return { blob };
    },

    /** `GET /reports/{report}/shares` - nieuwste eerst. */
    async shares(reportUuid: string): Promise<ReportShare[]> {
        const response = reportSharesResponseSchema.parse(
            await api.get(`/reports/${reportUuid}/shares`),
        );

        return response.data.map(toReportShare);
    },

    /** `POST /reports/{report}/shares` - een nieuwe link met de standaarden van het bedrijf. */
    async createShare(reportUuid: string): Promise<IssuedReportShare> {
        const response = reportShareResponseSchema.parse(
            await api.post(`/reports/${reportUuid}/shares`, {}, WRITE),
        );

        return { share: toReportShare(response.data), accessCode: response.meta.access_code };
    },

    /** `PATCH /reports/{report}/shares/{share}` */
    async updateShare(
        reportUuid: string,
        shareUuid: string,
        changes: ReportShareChanges,
    ): Promise<IssuedReportShare> {
        const response = reportShareResponseSchema.parse(
            await api.patch(
                `/reports/${reportUuid}/shares/${shareUuid}`,
                shareChangesBody(changes),
                WRITE,
            ),
        );

        return { share: toReportShare(response.data), accessCode: response.meta.access_code };
    },

    /** `GET /reports/{report}/statistics` */
    /** `GET /reports/pdf-service` - wachtende PDF's van het bedrijf, en of de PDF-dienst ontbreekt. */
    async pdfService(): Promise<PdfBacklog> {
        return toPdfBacklog(
            unwrap(
                pdfBacklogResourceSchema,
                await api.get('/reports/pdf-service', { cacheKey: false }),
            ),
        );
    },

    async statistics(reportUuid: string): Promise<ReportLinkStatistics> {
        return toReportLinkStatistics(
            unwrap(
                reportStatisticsResourceSchema,
                await api.get(`/reports/${reportUuid}/statistics`),
            ),
        );
    },
};

/**
 * `include_invoice` en `message` alleen als de factuur mee moet: zonder factuur kent de
 * API geen eigen bericht, en een lege body blijft een lege body.
 */
function invoiceBody(choice: ReportInvoiceChoice | undefined): Record<string, unknown> {
    return choice?.includeInvoice
        ? { include_invoice: true, message: choice.message.trim() === '' ? null : choice.message }
        : {};
}

/** `language` alleen voor een andere taal dan Nederlands: zonder is de mail Nederlands. */
function languageBody(language: string | undefined): Record<string, unknown> {
    return language && language !== 'nl' ? { language } : {};
}

function shareChangesBody(changes: ReportShareChanges): Record<string, unknown> {
    return {
        ...(changes.validDays === undefined ? {} : { valid_days: changes.validDays }),
        ...(changes.showCosts === undefined ? {} : { show_costs: changes.showCosts }),
        ...(changes.allowForward === undefined ? {} : { allow_forward: changes.allowForward }),
        ...(changes.requireAccessCode ? { require_access_code: true } : {}),
        ...(changes.removeAccessCode ? { remove_access_code: true } : {}),
        ...(changes.revoke ? { revoke: true } : {}),
    };
}
